/**
 * The event sidebar of the calendar module: a right-side slide-in drawer (Merkuro/Outlook
 * pattern) that replaced the create/edit modal and the details modal. One panel, two views:
 *
 * - create ({@link EventSidebarTargetKind 'create'}): the form pre-filled with the picked
 *   window, POST on save;
 * - edit ({@link EventSidebarTargetKind 'edit'}): the form pre-filled from an event, PATCH
 *   on save, delete behind a confirm gate (the /calendar grid click opens this view);
 * - details ({@link EventSidebarTargetKind 'details'}): the read-only summary (facts, room
 *   link, intent badges) with Edit switching the SAME drawer to the edit form and delete
 *   behind its own confirm gate (the dashboard's event rows open this view).
 *
 * The surface lives in components/ui/sheet.tsx (Radix dialog primitive): dim scrim over the
 * still-visible calendar, ~200ms slide-in from the right, full viewport width below the sm
 * breakpoint. Radix's modal behavior provides the a11y core - role="dialog" + aria-modal,
 * focus trapping, focus return on close, Escape and scrim-click dismissal. The drawer
 * unmounts its content when closed, so each open re-arms the form from the target.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, SheetContent } from '../components/ui/sheet';
import type { CalendarEvent } from './api';
import { EventSidebarDetails } from './EventSidebarDetails';
import { EventSidebarForm, type EventSidebarInitial } from './EventSidebarForm';

/** What the drawer is opened for; drives the view it starts in. */
export type EventSidebarTarget =
  | { readonly kind: 'create'; readonly initial: EventSidebarInitial }
  | { readonly kind: 'edit'; readonly event: CalendarEvent }
  | { readonly kind: 'details'; readonly event: CalendarEvent };

export interface EventSidebarProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Fresh bearer token from the SSO session; the drawer assumes it is valid. */
  readonly token: string;
  /** The open reason (create prefill / edit target / details target); null = nothing to show. */
  readonly target: EventSidebarTarget | null;
  /** The events already loaded for the visible window: the form's client-side conflict check. */
  readonly allEvents?: readonly CalendarEvent[];
  /** Called after a successful create/patch so the page refetches the range. */
  readonly onSaved: (event: CalendarEvent) => void;
  /** Called after a successful delete so the page refetches the range. */
  readonly onDeleted: (id: number) => void;
  /** Navigates to /r/<slug> (the details view's room link). */
  readonly onOpenRoom?: ((slug: string) => void) | undefined;
}

type SidebarView = 'form' | 'details';

export function EventSidebar({
  open,
  onOpenChange,
  token,
  target,
  allEvents = [],
  onSaved,
  onDeleted,
  onOpenRoom,
}: EventSidebarProps): JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<SidebarView>(() =>
    target?.kind === 'details' ? 'details' : 'form',
  );
  // The page element to hand focus back to on close. Radix restores focus only to a
  // DialogTrigger - the pages open this drawer imperatively, so the element is captured
  // here instead: onOpenAutoFocus fires before Radix moves focus into the panel, so the
  // active element is still whatever the page had focused.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Re-arm the view whenever the drawer (re)opens or the target changes: a details target
  // starts on the summary, create/edit land directly on the form.
  useEffect(() => {
    if (!open) {
      return;
    }
    setView(target?.kind === 'details' ? 'details' : 'form');
  }, [open, target]);

  function close(): void {
    onOpenChange(false);
  }

  const editingEvent =
    target !== null && target.kind !== 'create' && view === 'form' ? target.event : null;
  const createInitial = target !== null && target.kind === 'create' ? target.initial : null;

  return (
    <Sheet open={open && target !== null} onOpenChange={onOpenChange}>
      <SheetContent
        closeLabel={t('calendar.details.close')}
        className="w-full sm:max-w-[600px]"
        data-testid="event-sidebar"
        onOpenAutoFocus={() => {
          if (document.activeElement instanceof HTMLElement) {
            restoreFocusRef.current = document.activeElement;
          }
        }}
        onCloseAutoFocus={(closeEvent) => {
          closeEvent.preventDefault();
          restoreFocusRef.current?.focus();
          restoreFocusRef.current = null;
        }}
      >
        {target === null ? null : view === 'form' ? (
          <EventSidebarForm
            token={token}
            initial={createInitial}
            event={editingEvent}
            allEvents={allEvents}
            onSaved={onSaved}
            onDeleted={onDeleted}
            onClose={close}
            // Cancel closes the drawer - unless the form was reached from the details view,
            // then it steps back to the summary instead.
            onCancel={() =>
              target.kind === 'details' && editingEvent !== null ? setView('details') : close()
            }
          />
        ) : target.kind !== 'create' ? (
          <EventSidebarDetails
            event={target.event}
            token={token}
            onClose={close}
            onEdit={() => setView('form')}
            onDeleted={onDeleted}
            onOpenRoom={onOpenRoom}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
